import {
  collection,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { useCallback, useEffect, useRef } from "react";
import useActionsStore from "stores/useActionsStore";
import useActionsViewStore from "stores/useActionsViewStore";
import { actionDate } from "utils/actionDate";
import { getSessionCutoff } from "utils/actionLiveWindow";
import { useUser } from "stores/useUser";
import {
  checkForInboxNotifications,
  checkForOutboxNotifications,
  checkForShareActionsNotification,
  notify,
} from "utils/common";
import { FirestoreDB } from "utils/firebase";
import { captureEvent } from "utils/posthog";

const LISTENER_RETRY_DELAY_MS = 5000;
const MAX_LISTENER_RETRIES = 3;
// Errors a restart cannot fix, e.g. a missing index or no access. Retrying
// them only reloads every list again.
const PERMANENT_ERROR_CODES = new Set([
  "failed-precondition",
  "permission-denied",
  "invalid-argument",
  "not-found",
]);

// After a refresh, compares the first server snapshot with what the old
// listener had put in the store. A difference means the old listener had
// stopped receiving server changes without reporting an error.
const reportStaleActionItems = (serverItems) => {
  const storeIds = new Set(
    useActionsStore.getState().actionItems.map((item) => item.id),
  );
  const serverIds = new Set(serverItems.map((item) => item.id));
  const missingCount = serverItems.filter(
    (item) => !storeIds.has(item.id),
  ).length;
  const extraCount = [...storeIds].filter((id) => !serverIds.has(id)).length;

  if (missingCount || extraCount) {
    captureEvent("actions_listener_stale", { missingCount, extraCount });
  }
};

// An action counts as new only if it was created in the last few minutes.
// This keeps old actions out when a listener restart replays everything.
const RECENT_ACTION_WINDOW_MS = 10 * 60 * 1000;

// Actions that arrive while the app is open, from the composer, the
// assistant or another device, go to the "Just added" group.
export const markNewActions = (previousItems, serverItems, now = Date.now()) => {
  const known = new Set(previousItems.map((item) => item.id));
  const { addRecentId } = useActionsViewStore.getState();
  serverItems
    // Completing an action can move it in from the other listener; that is
    // not a new action.
    .filter((item) => !known.has(item.id) && !item.completed)
    .filter((item) => {
      const created = actionDate(item.created_on);
      return !created || now - created.getTime() < RECENT_ACTION_WINDOW_MS;
    })
    .sort((a, b) => (actionDate(a.created_on) || 0) - (actionDate(b.created_on) || 0))
    .forEach((item) => addRecentId(item.id));
};

const completedTime = (item) => actionDate(item.completed_updated_at)?.getTime() || 0;

// One list from the open and the recently completed listeners, newest
// first. While an action moves from one to the other it can be in both for
// a moment; the copy with the newer completed_updated_at is the current one.
export const mergeLiveActions = (openItems, recentItems) => {
  const byId = new Map(openItems.map((item) => [item.id, item]));
  recentItems.forEach((item) => {
    const open = byId.get(item.id);
    if (!open || completedTime(item) >= completedTime(open)) byId.set(item.id, item);
  });
  return [...byId.values()].sort((a, b) => b.created_on - a.created_on);
};

const useFirebaseActions = () => {
  const {
    setActionItems,
    setLoading,
    outboxItems,
    setOutboxItems,
    setOutboxLoading,
    setOutboxError,
    setSharedAccessList,
    setSharedAccessLoading,
    setSharedAccessError,
    setLabels,
    refreshKey,
    refreshActions,
  } = useActionsStore();
  const { user } = useUser();
  // Failed restarts in a row, per collection. A server snapshot resets it.
  const retryCountsRef = useRef({});
  const retryTimerRef = useRef(null);

  const markListenerHealthy = (collectionName) => {
    retryCountsRef.current[collectionName] = 0;
  };

  // onSnapshot stops for good after an error, so subscribe again a few times.
  // One restart covers all three listeners, so errors that arrive together
  // share one timer.
  const handleListenerError = useCallback(
    (collectionName, error) => {
      console.error(`${collectionName} listener failed:`, error);
      captureEvent("actions_listener_error", {
        collection: collectionName,
        code: error?.code || "unknown",
      });
      const retryCount = retryCountsRef.current[collectionName] || 0;
      if (retryCount >= MAX_LISTENER_RETRIES) return;
      if (PERMANENT_ERROR_CODES.has(error?.code)) return;
      retryCountsRef.current[collectionName] = retryCount + 1;
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = setTimeout(
        refreshActions,
        LISTENER_RETRY_DELAY_MS,
      );
    },
    [refreshActions],
  );

  useEffect(() => () => clearTimeout(retryTimerRef.current), []);

  // for getting actions: open ones, plus the ones completed in the last
  // 30 days. Older completed actions load page by page on the Actions page
  // (useActionHistory), so this list stays small for busy people.
  useEffect(() => {
    if (!user?.uid) return undefined;
    // A restart keeps the current list on screen until the new one arrives.
    if (refreshKey === 0) setLoading(true);
    const ref = collection(FirestoreDB, `users/${user.uid}/ActionItems`);
    const cutoff = getSessionCutoff(user.uid);
    const isRefresh = refreshKey > 0;
    const sources = {
      open: { items: [], received: false, fromServer: false },
      recent: { items: [], received: false, fromServer: false },
    };
    let staleChecked = false;
    // The store changes only when both lists have arrived, so a restart
    // never shows half the actions for a moment.
    let ready = false;

    const listen = (name, collectionName, liveQuery) =>
      onSnapshot(
        liveQuery,
        // Metadata events deliver the server confirmation even when it
        // matches the cached snapshot, so the stale check always runs on it.
        { includeMetadataChanges: true },
        (querySnapshot) => {
          const source = sources[name];
          const res = [];
          querySnapshot.forEach((doc) => {
            res.push({ id: doc.id, ...doc.data() });
          });
          const changes = querySnapshot.docChanges();
          const wasReceived = source.received;
          source.items = res;
          source.received = true;
          if (!querySnapshot.metadata.fromCache) {
            source.fromServer = true;
            markListenerHealthy(collectionName);
          }
          if (!sources.open.received || !sources.recent.received) return;
          const wasReady = ready;
          ready = true;
          const merged = mergeLiveActions(sources.open.items, sources.recent.items);

          // After a restart, compare once both lists came from the server.
          if (
            isRefresh &&
            !staleChecked &&
            sources.open.fromServer &&
            sources.recent.fromServer
          ) {
            staleChecked = true;
            reportStaleActionItems(merged);
          }
          // A metadata-only event changes no docs; keep the store as is.
          if (wasReady && wasReceived && changes.length === 0) return;
          // The first load of the session is not "new". After a restart
          // the store still holds the old list, so the diff still works.
          if (wasReady || isRefresh) {
            markNewActions(useActionsStore.getState().actionItems, merged);
          }
          setActionItems(merged);
          setLoading(false);

          // A restarted listener reports every existing doc as "added"
          // again, and only open actions can be new Inbox items.
          if (name !== "open" || !wasReceived) return;
          changes.forEach((change) =>
            checkForInboxNotifications(change, user?.lastTimeAccessed),
          );
        },
        (error) => {
          // A failed list counts as empty, so the other one still shows.
          // For example, the recent query fails until its index is built.
          const source = sources[name];
          if (!source.received) {
            source.received = true;
            if (sources.open.received && sources.recent.received) {
              ready = true;
              setActionItems(mergeLiveActions(sources.open.items, sources.recent.items));
              setLoading(false);
            }
          }
          handleListenerError(collectionName, error);
        },
      );

    const unsubscribeOpen = listen(
      "open",
      "ActionItems",
      query(ref, where("completed", "==", false)),
    );
    const unsubscribeRecent = listen(
      "recent",
      "ActionItemsRecent",
      query(
        ref,
        where("completed", "==", true),
        where("completed_updated_at", ">=", cutoff),
        orderBy("completed_updated_at", "desc"),
      ),
    );

    return () => {
      unsubscribeOpen();
      unsubscribeRecent();
    };
  }, [user, refreshKey, setActionItems, setLoading, handleListenerError]);

  // for getting outbox items
  useEffect(() => {
    let unSubscribe;
    const getOuboxItems = async () => {
      try {
        setOutboxLoading(true);
        console.log("SUBSCRIBE OUTBOX items");

        unSubscribe = onSnapshot(
          collection(FirestoreDB, `users/${user.uid}/Outbox`),
          (querySnapshot) => {
            const res = [];
            querySnapshot.forEach((doc) => {
              res.push({ id: doc.id, ...doc.data() });
            });
            if (!querySnapshot.metadata.fromCache) {
              markListenerHealthy("Outbox");
            }
            querySnapshot.docChanges().forEach((change) =>
              checkForOutboxNotifications(
                change,
                user?.lastTimeAccessed,
                // sending the prev outbox items to make compare
                outboxItems,
              ),
            );
            setOutboxItems(
              res.slice().sort((a, b) => b.created_on - a.created_on),
            );
            setOutboxLoading(false);
          },
          (error) => handleListenerError("Outbox", error),
        );
      } catch (error) {
        console.log(error);
        notify("Unable to fetch Action items", "error");
        setOutboxError(true);
      }
    };

    getOuboxItems();
    return () => {
      console.log("UNSUBSCRIBE OUTBOX items");
      unSubscribe?.();
    };
  }, [
    user,
    refreshKey,
    setOutboxError,
    setOutboxItems,
    setOutboxLoading,
    handleListenerError,
  ]);

  // for getting SharedAccess persons
  useEffect(() => {
    let unSubscribe;
    const getSharedAccessList = async () => {
      try {
        setSharedAccessLoading(true);
        console.log("SUBSCRIBE Action access items");
        let replayingExisting = refreshKey > 0;

        unSubscribe = onSnapshot(
          collection(FirestoreDB, `users/${user.uid}/ActionAccess`),
          (querySnapshot) => {
            const res = [];
            querySnapshot.forEach((doc) => {
              res.push({ id: doc.id, ...doc.data() });
            });
            if (!querySnapshot.metadata.fromCache) {
              markListenerHealthy("ActionAccess");
            }
            if (!replayingExisting) {
              querySnapshot
                .docChanges()
                .forEach((change) =>
                  checkForShareActionsNotification(
                    change,
                    user?.lastTimeAccessed,
                  ),
                );
            }
            replayingExisting = false;
            setSharedAccessList(
              res.slice().sort((a, b) => b.created_on - a.created_on),
            );
            setSharedAccessLoading(false);
          },
          (error) => handleListenerError("ActionAccess", error),
        );
      } catch (error) {
        console.log(error);
        notify("Unable to fetch Shared Action list items", "error");
        setSharedAccessError(true);
      }
    };

    getSharedAccessList();
    return () => {
      console.log("UNSUBSCRIBE Shared action access list items");
      unSubscribe?.();
    };
  }, [
    user,
    refreshKey,
    setSharedAccessError,
    setSharedAccessLoading,
    setSharedAccessList,
    handleListenerError,
  ]);

  // for getting the person's action labels
  useEffect(() => {
    if (!user?.uid) return undefined;
    const unSubscribe = onSnapshot(
      collection(FirestoreDB, `users/${user.uid}/ActionLabels`),
      (querySnapshot) => {
        const res = [];
        querySnapshot.forEach((doc) => {
          res.push({ id: doc.id, ...doc.data() });
        });
        if (!querySnapshot.metadata.fromCache) {
          markListenerHealthy("ActionLabels");
        }
        setLabels(res);
      },
      (error) => handleListenerError("ActionLabels", error),
    );
    return () => unSubscribe();
  }, [user, refreshKey, setLabels, handleListenerError]);
  return null;
};

export default useFirebaseActions;
